/**
 * Push 订阅管理
 * - 注册 service worker
 * - 请求通知权限
 * - 订阅 push 并把 endpoint/keys 同步到后端
 */

import { pushApi, authApi } from '../api/client';

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushState = 'unsupported' | 'default' | 'granted' | 'denied' | 'subscribed' | 'error';

export async function registerSw(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    console.warn('[push] SW register failed:', err);
    return null;
  }
}

export async function getNotificationState(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function ensureSubscribed(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported';
  }
  const reg = (await navigator.serviceWorker.getRegistration('/')) ||
    (await navigator.serviceWorker.ready);
  if (!reg) return 'error';

  // 已经有订阅, 直接同步给后端
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      await syncToBackend(existing);
      return 'subscribed';
    } catch {
      return 'error';
    }
  }

  // 还没订阅, 申请权限
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'default';
  }

  try {
    const vapid = await authApi.vapidKey();
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
    });
    await syncToBackend(sub);
    return 'subscribed';
  } catch (err) {
    console.warn('[push] subscribe failed:', err);
    return 'error';
  }
}

export async function unsubscribe(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  try {
    await pushApi.unsubscribeAll();
  } catch {
    /* 后端清理失败不阻塞本地取消 */
  }
  return sub.unsubscribe();
}

export async function isSubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

async function syncToBackend(sub: PushSubscription) {
  const json = sub.toJSON();
  await pushApi.subscribe({
    endpoint: json.endpoint!,
    keys: {
      p256dh: json.keys!.p256dh,
      auth: json.keys!.auth,
    },
  });
}
